import { LiaElement } from '@liasoft/lit-ui';
import { html, nothing } from 'lit';
import { ACCOUNTS_PATH, accountPath } from '../accounts.js';

/**
 * What to do first, on a team nothing has run against yet.
 *
 * A new team has no seats and nobody in it, which reads as "nothing happens" to an owner looking at
 * four empty pages. This says the three things that make something happen, in the order they happen -
 * seats are bought, people are put in them, CI runs under a project token - and disappears once a run
 * has arrived.
 *
 * A personal team has no seats to buy and nobody to invite, so its three steps are the ones personal
 * use is made of: run covdbg locally, run it in CI under a project token that covers only its owner,
 * and create a team when other people come into it.
 *
 * The snippet is the client's real contract: CI hands the client a credential in
 * \`COVDBG_PROJECT_TOKEN\`, and the commit's author is who the run is about.
 */
export class UserGettingStarted extends LiaElement
{
   static properties = {
      /** The account id, for the links into its pages. */
      account: { type: String },

      /** The team's name, so the panel says whose team this is. */
      name: { type: String },

      /** Whether the reader owns the team. A member is told less and pointed at the owner. */
      owner: { type: Boolean },

      /** A personal team: the free tier, with nothing to buy and nobody to invite. */
      personal: { type: Boolean }
   };

   constructor()
   {
      super();
      this.account = '';
      this.name = '';
      this.owner = false;
      this.personal = false;
   }

   render()
   {
      if (this.personal)
      {
         return html`
            <lia-card title="Get started" icon="fa-solid fa-rocket" variant="primary">
               <p class="mb-3">Nothing has run for you yet. Three steps to the first run:</p>

               <div class="row g-3">
                  <div class="col-lg-4">${this.local}</div>
                  <div class="col-lg-4">${this.ci(2)}</div>
                  <div class="col-lg-4">${this.team}</div>
               </div>
            </lia-card>`;
      }

      return html`
         <lia-card
            title=${`Get started with ${this.name || 'your team'}`}
            icon="fa-solid fa-rocket"
            variant="primary"
         >
            <p class="mb-3">
               Nothing has run for ${this.name || 'your team'} yet. A seat is a person, counted once however
               many machines they use; public repositories cost nothing. Three steps to the first run:
            </p>

            <div class="row g-3">
               <div class="col-lg-4">${this.buy}</div>
               <div class="col-lg-4">${this.seat}</div>
               <div class="col-lg-4">${this.ci(3)}</div>
            </div>
         </lia-card>`;
   }

   get buy()
   {
      return this.step(1, 'Buy seats', html`
         <p class="small text-body-secondary">
            A team has no seats until it is given some: one per person who commits to a private
            repository. Open billing to buy seats or check the purchasing options for your team.
         </p>
         ${this.owner
            ? html`<a class="small" href=${accountPath('billing', this.account)}>Open billing</a>`
            : nothing}`);
   }

   get seat()
   {
      return this.step(2, 'Invite members', html`
         <p class="small text-body-secondary">
            Invite each member by address; the link is mailed and accepting it puts them in a seat.
            Take one for yourself from the overview - no invitation needed.
         </p>
         ${this.owner
            ? html`<a class="small" href=${accountPath('members', this.account)}>Invite members</a>`
            : nothing}`);
   }

   ci(number)
   {
      return this.step(number, 'Run CI with a project token', html`
         <p class="small text-body-secondary">
            Issue a project token, keep it in the CI's secret store, and hand it to covdbg in an
            environment variable. It names ${this.personal ? 'you, so it covers your commits and nobody else\'s' : 'the team; each commit\'s author is who the run is about'}.
         </p>
         ${this.snippet('COVDBG_PROJECT_TOKEN=covdbg_pt_...\ncovdbg -o coverage.covdb ./your-tests')}
         ${this.owner
            ? html`
               <a class="small" href=${accountPath('tokens', this.account)}>Issue a project token</a>`
            : nothing}`);
   }

   get local()
   {
      return this.step(1, 'Run covdbg locally', html`
         <p class="small text-body-secondary mb-0">
            Sign in from the machine you commit on and run covdbg in a repository. Public ones are free
            without limit; the first private one becomes the one your personal use is locked to.
         </p>`);
   }

   get team()
   {
      return this.step(3, 'Work with others', html`
         <p class="small text-body-secondary">
            To run covdbg for other people's commits, or in private repositories without limit, create
            a team: a name and one click, and seats are bought there.
         </p>
         <a class="small" href=${ACCOUNTS_PATH}>Create a team</a>`);
   }

   step(number, title, body)
   {
      return html`
         <div class="h-100">
            <div class="d-flex align-items-center gap-2 mb-2">
               <span class="badge rounded-pill text-bg-primary">${number}</span>
               <strong>${title}</strong>
            </div>
            ${body}
         </div>`;
   }

   snippet(text)
   {
      return html`
         <div class="position-relative">
            <pre class="bg-body-tertiary rounded p-2 pe-5 small mb-2"><code>${text}</code></pre>
            <lia-copy-button class="position-absolute top-0 end-0 m-1" .text=${text}></lia-copy-button>
         </div>`;
   }
}

customElements.define('user-getting-started', UserGettingStarted);
